import React, { useState, useEffect, useCallback } from 'react';

interface QueueInfo {
  name: string;
  messages: number;
  consumers?: number;
  deliver_rate?: number;
  ttl_ms?: number | null;
  messages_ready?: number;
  messages_unacknowledged?: number;
  message_stats?: {
    publish: number;
    publish_rate: number;
    deliver: number;
    deliver_rate: number;
    ack: number;
    ack_rate: number;
    redeliver: number;
  };
  state?: string;
  is_dlq?: boolean;
  is_retry?: boolean;
  type?: string; // RabbitMQ queue engine: "classic" | "quorum" | "stream"
  node?: string;
  arguments?: Record<string, any>;
}

interface DlqEvent {
  id: number;
  timestamp: string;
  alert_type: string;
  client_id: string;
  routing_key: string;
  retry_count: number;
  error: string | null;
  created_at: string;
}

interface SummaryData {
  rabbitmq_reachable: boolean;
  vhost?: string;
  version: string | null;
  node: string | null;
  broker_message_stats: Record<string, any>;
  queue_totals: { messages?: number; messages_ready?: number; messages_unacknowledged?: number };
  queue_type_counts?: Record<string, number>;
  queues: {
    main: QueueInfo[];
    retry: QueueInfo[];
    dlq: QueueInfo[];
  };
  dlq_db: { total: number; last_24h: number };
  alerts: {
    has_dlq_messages: boolean;
    has_retry_messages: boolean;
    has_recent_dlq_events: boolean;
    no_consumers: boolean;
  };
}

const apiCall = async (path: string) => {
  const token = localStorage.getItem('token');
  const resp = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
};

interface ExchangeInfo {
  name: string;
  type: string;
  durable: boolean;
  message_stats: {
    publish_in: number;
    publish_in_rate: number;
    publish_out: number;
    publish_out_rate: number;
  };
}

interface BindingInfo {
  source: string;
  destination: string;
  destination_type: string;
  routing_key: string;
  arguments: Record<string, any>;
}

export const RabbitMQPage: React.FC = () => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [allQueues, setAllQueues] = useState<QueueInfo[]>([]);
  const [exchanges, setExchanges] = useState<ExchangeInfo[]>([]);
  const [bindings, setBindings] = useState<BindingInfo[]>([]);
  const [dlqEvents, setDlqEvents] = useState<DlqEvent[]>([]);
  const [dlqStats, setDlqStats] = useState<{ total: number; last_24h: number; by_type: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [expandedDlq, setExpandedDlq] = useState<Set<number>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [summaryData, queuesData, exchangesData, bindingsData, dlqEventsData, dlqStatsData] = await Promise.all([
        apiCall('/api/rabbitmq/summary'),
        apiCall('/api/rabbitmq/queues').catch(() => ({ queues: [] })),
        apiCall('/api/rabbitmq/exchanges').catch(() => ({ exchanges: [] })),
        apiCall('/api/rabbitmq/bindings').catch(() => ({ bindings: [] })),
        apiCall('/api/rabbitmq/dlq-events').catch(() => ({ db: { events: [] } })),
        apiCall('/api/rabbitmq/dlq-stats').catch(() => ({ total: 0, last_24h: 0, by_type: [] })),
      ]);
      setSummary(summaryData);
      setAllQueues(queuesData.queues || []);
      setExchanges(exchangesData.exchanges || []);
      setBindings(bindingsData.bindings || []);
      setDlqEvents(dlqEventsData.db?.events || []);
      setDlqStats(dlqStatsData);
      setError(null);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  const toggleDlqExpand = (id: number) => {
    setExpandedDlq(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const alerts = summary?.alerts;
  const hasAlerts = alerts && (alerts.has_dlq_messages || alerts.has_retry_messages || alerts.has_recent_dlq_events || alerts.no_consumers);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RabbitMQ Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">
            Queue depths, retry status, dead-letter events, and delivery health
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-500">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (10s)
          </label>
          <button
            onClick={loadData}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Refresh
          </button>
          {lastRefresh && (
            <span className="text-xs text-gray-400">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Alerts Banner */}
      {hasAlerts && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <div className="font-semibold text-amber-800">Attention Required</div>
          {alerts.has_dlq_messages && (
            <div className="text-sm text-amber-700">
              Messages in DLQ -- some events failed delivery after max retries
            </div>
          )}
          {alerts.has_retry_messages && (
            <div className="text-sm text-amber-700">
              Messages in retry queue -- events are being retried
            </div>
          )}
          {alerts.has_recent_dlq_events && (
            <div className="text-sm text-amber-700">
              DLQ events in last 24h -- check the Dead Letter Events table below
            </div>
          )}
          {alerts.no_consumers && (
            <div className="text-sm text-red-700 font-medium">
              Queue has no consumers -- messages will pile up without being processed
            </div>
          )}
        </div>
      )}

      {/* Connection Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Broker Status</div>
          {summary?.rabbitmq_reachable ? (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="font-semibold text-green-700">Connected</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="font-semibold text-red-700">Unreachable</span>
            </div>
          )}
          {summary?.version && <div className="text-xs text-gray-400 mt-1">v{summary.version}</div>}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Queued</div>
          <div className="text-2xl font-bold text-gray-900">
            {summary?.queue_totals?.messages ?? '--'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {summary?.queue_totals?.messages_ready ?? 0} ready / {summary?.queue_totals?.messages_unacknowledged ?? 0} unacked
          </div>
        </div>

        <div className={`bg-white rounded-lg shadow-sm border p-4 ${(summary?.queues?.retry?.reduce((s, q) => s + q.messages, 0) ?? 0) > 0 ? 'ring-2 ring-amber-300' : ''}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">In Retry</div>
          <div className={`text-2xl font-bold ${(summary?.queues?.retry?.reduce((s, q) => s + q.messages, 0) ?? 0) > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
            {summary?.queues?.retry?.reduce((s, q) => s + q.messages, 0) ?? '--'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {summary?.queues?.retry?.length ?? 0} retry queue(s)
          </div>
        </div>

        <div className={`bg-white rounded-lg shadow-sm border p-4 ${(summary?.queues?.dlq?.reduce((s, q) => s + q.messages, 0) ?? 0) > 0 ? 'ring-2 ring-red-300' : ''}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">In DLQ</div>
          <div className={`text-2xl font-bold ${(summary?.queues?.dlq?.reduce((s, q) => s + q.messages, 0) ?? 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {summary?.queues?.dlq?.reduce((s, q) => s + q.messages, 0) ?? '--'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {dlqStats?.total ?? 0} total DLQ events logged
          </div>
        </div>
      </div>

      {/* Queue Details Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Queue Details</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="px-4 py-3 text-left font-medium text-gray-500">Queue</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Type</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Messages</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Ready</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Unacked</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Consumers</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Publish</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Deliver/s</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Ack</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">State</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {allQueues.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No queues found or RabbitMQ unreachable</td></tr>
              ) : (
                allQueues.map(q => {
                  const qType = q.is_dlq ? 'DLQ' : q.is_retry ? 'Retry' : 'Main';
                  const typeColor = q.is_dlq ? 'bg-red-100 text-red-700' : q.is_retry ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700';
                  const hasMessages = q.messages > 0;
                  const rowHighlight = q.is_dlq && hasMessages ? 'bg-red-50' : q.is_retry && hasMessages ? 'bg-amber-50' : '';

                  const engine = q.type || 'classic';
                  const engineColor = engine === 'quorum'
                    ? 'bg-green-100 text-green-700'
                    : engine === 'stream'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-600';
                  return (
                    <tr key={q.name} className={rowHighlight}>
                      <td className="px-4 py-3 font-mono text-xs">{q.name}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${typeColor}`}>{qType}</span>
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium uppercase ${engineColor}`}
                            title={q.node ? `Leader node: ${q.node}` : undefined}
                          >
                            {engine}
                          </span>
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${hasMessages && (q.is_dlq || q.is_retry) ? 'text-red-600' : ''}`}>
                        {q.messages}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{q.messages_ready ?? '-'}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{q.messages_unacknowledged ?? '-'}</td>
                      <td className={`px-4 py-3 text-right ${(q.consumers ?? 0) === 0 && !q.is_dlq ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {q.consumers ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{q.message_stats?.publish ?? '-'}</td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {q.message_stats?.deliver_rate != null ? q.message_stats.deliver_rate.toFixed(1) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{q.message_stats?.ack ?? '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block w-2 h-2 rounded-full mr-1 ${q.state === 'running' ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <span className="text-gray-600 text-xs">{q.state || '-'}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Exchanges */}
      {exchanges.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Exchanges</h2>
            <p className="text-xs text-gray-500 mt-1">Topic exchanges route messages to queues based on routing key patterns</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Exchange</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Type</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500">Durable</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Published In</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">In Rate</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Routed Out</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Out Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {exchanges.map(ex => {
                  const isMain = ex.name === 'main';
                  return (
                    <tr key={ex.name} className={isMain ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-3 font-mono text-xs">
                        {ex.name}
                        {isMain && <span className="ml-2 text-[10px] font-semibold bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded uppercase">primary</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ex.type === 'topic' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                          {ex.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{ex.durable ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3 text-right font-medium">{ex.message_stats.publish_in}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{ex.message_stats.publish_in_rate.toFixed(1)}/s</td>
                      <td className="px-4 py-3 text-right font-medium">{ex.message_stats.publish_out}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{ex.message_stats.publish_out_rate.toFixed(1)}/s</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Routing & Bindings (Security Validation) */}
      {bindings.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Routing &amp; Bindings</h2>
            <p className="text-xs text-gray-500 mt-1">
              Exchange-to-queue bindings with routing key filters. Each queue only receives events matching its routing key pattern,
              ensuring site/organization isolation.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Exchange</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-500"></th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Queue</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Routing Key Filter</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Isolation</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {bindings.map((b, i) => {
                  const orgMatch = b.routing_key.match(/\.(\d+)(?:\.|$|#)/);
                  const orgId = orgMatch ? orgMatch[1] : null;
                  const isWildcard = b.routing_key.includes('#') || b.routing_key.includes('*');
                  const isMainEvent = b.routing_key.startsWith('event.') && !b.routing_key.includes('retry');
                  const isDlqRoute = b.routing_key.startsWith('dlq.');
                  const isRetryRoute = b.routing_key.startsWith('retry.') || b.routing_key.includes('.retry.');

                  let purposeLabel = 'Event delivery';
                  let purposeColor = 'bg-blue-100 text-blue-700';
                  if (isDlqRoute) {
                    purposeLabel = 'Dead-letter';
                    purposeColor = 'bg-red-100 text-red-700';
                  } else if (isRetryRoute) {
                    purposeLabel = 'Retry routing';
                    purposeColor = 'bg-amber-100 text-amber-700';
                  }

                  return (
                    <tr key={`${b.source}-${b.destination}-${b.routing_key}-${i}`} className={isMainEvent ? 'bg-green-50' : ''}>
                      <td className="px-4 py-3 font-mono text-xs font-medium">{b.source}</td>
                      <td className="px-4 py-3 text-center text-gray-400 text-lg">&#8594;</td>
                      <td className="px-4 py-3 font-mono text-xs">{b.destination}</td>
                      <td className="px-4 py-3">
                        <code className={`px-2 py-1 rounded text-xs font-bold ${isMainEvent ? 'bg-green-100 text-green-800 ring-1 ring-green-300' : 'bg-gray-100 text-gray-700'}`}>
                          {b.routing_key}
                        </code>
                        {isWildcard && <span className="ml-1 text-[10px] text-gray-400">(wildcard)</span>}
                      </td>
                      <td className="px-4 py-3">
                        {orgId ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-xs font-medium text-green-700">Org {orgId} only</span>
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${purposeColor}`}>{purposeLabel}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Global</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 bg-gray-50 border-t text-xs text-gray-500">
            <strong>Security note:</strong> In DB-driven mode the worker binds each queue to exactly one concrete tenant key: <code className="bg-green-100 text-green-800 px-1 rounded">event.&#123;sourceSystem&#125;.&#123;externalId&#125;</code> (e.g. <code className="bg-green-100 text-green-800 px-1 rounded">event.nova.439</code>). That keeps delivery scoped to the assigned source-system plus tenant combination and prevents cross-tenant mixing at the broker level. The historic Nova-only legacy key <code className="bg-gray-100 px-1 rounded">nova.event.&#123;orgId&#125;</code> has been retired — the poller no longer publishes to it and the worker proactively unbinds it from queues it manages.
          </div>
        </div>
      )}

      {/* DLQ Stats */}
      {dlqStats && (dlqStats.total > 0 || dlqStats.by_type.length > 0) && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">DLQ Statistics (Database)</h2>
            <p className="text-xs text-gray-500 mt-1">Events that exhausted all retries and were moved to dead-letter</p>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Total DLQ Events</div>
              <div className="text-3xl font-bold text-red-600">{dlqStats.total}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Last 24 Hours</div>
              <div className={`text-3xl font-bold ${dlqStats.last_24h > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {dlqStats.last_24h}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">By Alert Type</div>
              {dlqStats.by_type.length === 0 ? (
                <div className="text-sm text-gray-400">None</div>
              ) : (
                <div className="space-y-1">
                  {dlqStats.by_type.map((t: any) => (
                    <div key={t.alert_type} className="flex justify-between text-sm">
                      <span className="font-mono text-gray-700">{t.alert_type}</span>
                      <span className="text-red-600 font-medium">{t.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DLQ Events Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Dead Letter Events</h2>
            <p className="text-xs text-gray-500 mt-1">
              Messages that failed delivery after maximum retries ({dlqEvents.length} shown)
            </p>
          </div>
        </div>
        {dlqEvents.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-400">
            <div className="text-4xl mb-2">&#10003;</div>
            <div className="font-medium text-green-600">No dead-letter events</div>
            <div className="text-sm mt-1">All messages are being delivered successfully</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Alert Type</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Client</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Routing Key</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Retries</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dlqEvents.map(evt => (
                  <React.Fragment key={evt.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleDlqExpand(evt.id)}
                    >
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {new Date(evt.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">{evt.alert_type}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{evt.client_id}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{evt.routing_key}</td>
                      <td className="px-4 py-3 text-right font-medium text-amber-600">{evt.retry_count}</td>
                      <td className="px-4 py-3 text-xs text-red-600 max-w-[300px] truncate" title={evt.error || undefined}>
                        {evt.error || '-'}
                      </td>
                    </tr>
                    {expandedDlq.has(evt.id) && (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 bg-gray-50">
                          <div className="text-xs">
                            <div className="font-medium text-gray-700 mb-1">Full Error:</div>
                            <pre className="whitespace-pre-wrap text-red-600 bg-white p-3 rounded border max-h-40 overflow-auto">
                              {evt.error || 'No error message'}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Broker Info */}
      {summary?.rabbitmq_reachable && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Broker Info</h2>
          </div>
          <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Version</div>
              <div className="font-medium">{summary.version || '-'}</div>
            </div>
            <div>
              <div className="text-gray-500">Node</div>
              <div className="font-mono text-xs">{summary.node || '-'}</div>
            </div>
            <div>
              <div className="text-gray-500">vhost</div>
              <div className="font-mono text-xs">{summary.vhost || '-'}</div>
            </div>
            <div>
              <div className="text-gray-500">Queue engines</div>
              <div className="font-medium text-xs flex flex-wrap gap-1">
                {summary.queue_type_counts && Object.keys(summary.queue_type_counts).length > 0 ? (
                  Object.entries(summary.queue_type_counts).map(([t, n]) => {
                    const color = t === 'quorum' ? 'bg-green-100 text-green-700'
                      : t === 'stream' ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-600';
                    return (
                      <span key={t} className={`inline-block px-2 py-0.5 rounded ${color}`}>
                        {n} {t}
                      </span>
                    );
                  })
                ) : '-'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Total Published</div>
              <div className="font-medium">{summary.broker_message_stats?.publish ?? '-'}</div>
            </div>
            <div>
              <div className="text-gray-500">Total Delivered</div>
              <div className="font-medium">{summary.broker_message_stats?.deliver ?? '-'}</div>
            </div>
            <div>
              <div className="text-gray-500">Publish Rate</div>
              <div className="font-medium">{summary.broker_message_stats?.publish_details?.rate?.toFixed(1) ?? '0'}/s</div>
            </div>
            <div>
              <div className="text-gray-500">Deliver Rate</div>
              <div className="font-medium">{summary.broker_message_stats?.deliver_details?.rate?.toFixed(1) ?? '0'}/s</div>
            </div>
            <div>
              <div className="text-gray-500">Ack Rate</div>
              <div className="font-medium">{summary.broker_message_stats?.ack_details?.rate?.toFixed(1) ?? '0'}/s</div>
            </div>
            <div>
              <div className="text-gray-500">Confirm Rate</div>
              <div className="font-medium">{summary.broker_message_stats?.confirm_details?.rate?.toFixed(1) ?? '0'}/s</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
